import { ART_PX, PixelBuffer, Pt, bayer, crossFade, drawLine, drawPolyline, fillPolygon, hash01, mix, rgb } from "./pixel_raster"
import {
    Bounds,
    CONTACT,
    HORIZON,
    MOON,
    PIVOT,
    ROCK_BOW,
    ROCK_BOW_HATCH,
    ROCK_NEAR,
    ROCK_SHORE_A,
    ROCK_SHORE_B,
    ROCK_SHORE_HATCH,
    SCENE,
    SHIP_AGROUND,
    SHIP_FAR,
    ShipState,
    buildLand,
    clamp01,
    flat,
    foreCoursePoints,
    lanternAt,
    lastBreaker,
    lerp,
    pennantPoints,
    plumeAt,
    prog,
    shipPoint,
    shipStateAt,
    smooth,
    sprayAt,
    topsailWeight,
    yardSwing,
} from "./shipwreck_geometry"
import { SwellRow, buildStormSky, flashAt, flashSolid, paintSwell, rowAt } from "./pixel_storm_art"

/**
 * The login screen's shipwreck as pixel art, in the game's own art style: the Liberated Pixel Cup
 * palette and rules (light from above-left, dark coloured outlines rather than black, shadows that
 * shift toward teal and purple, dithering used sparingly).
 *
 * It reuses the line-art scene's geometry and timeline (shipwreck_geometry.ts) -- where the ship is,
 * how it rolls, when it strikes, how the sail tears -- and only changes how each thing is drawn:
 * every piece is rasterized onto a grid of whole art pixels, so the ship is REDRAWN at every size
 * and pose as it closes in and strikes, never a picture scaled up.
 *
 * Everything here is pure (buffers in, buffers out); pixel_shipwreck_scene.ts puts it on screen.
 */

/** One art pixel is this many logical (GUI) pixels: the density the game draws its world at. */
export const PX = ART_PX

const HORIZON_ROW = Math.round(HORIZON / PX)

// ---- palette ----------------------------------------------------------------------------------

const ramp = (...hex: number[]) => hex.map(h => rgb(h))

/** Under the storm's swell, where nothing has been painted yet. */
const SEA_BASE = rgb(0x101820)

const MOON_RIM = rgb(0xf1efe2)
const MOON_LIT = rgb(0xd8d4c2)
const MOON_MID = rgb(0xb9b5a6)
const MOON_CRATER = rgb(0xa9a698)
const MOON_TERMINATOR = rgb(0x5f6074)
const MOON_DARK = rgb(0x262d42)

/** LPC's pine ramp, taken a step toward the night: outline, shadow .. lit edge. */
const PINE = ramp(0x040e10, 0x08201f, 0x0c2d2b, 0x074337, 0x0b5c2f)
const TRUNK = rgb(0x2b1c1d)
/** The hill: shadow .. moonlit grass. */
const HILL = ramp(0x07161a, 0x0a1f22, 0x0c2a2a, 0x103a33, 0x17503c, 0x2a6a46)
const SAND_HI = rgb(0x4a4652)
const SAND = rgb(0x35323e)
const SAND_WET = rgb(0x24242f)

/** Rock: outline, shadow .. lit top, cool and a little purple like LPC's stone. */
const ROCK = ramp(0x0b0b13, 0x1b1b27, 0x292938, 0x3c3c4e, 0x585a6e, 0x7a7c90)

/** LPC's bark ramp for the hull: outline, shadow .. rail highlight. */
const WOOD = ramp(0x140c0e, 0x2b1c1d, 0x3a2912, 0x56381d, 0x63462b, 0x7e6952)
/** Moonlit sailcloth: outline, deep shadow .. highlight. */
const CLOTH = ramp(0x27232e, 0x48424f, 0x6b6472, 0x938b8e, 0xb9af9e, 0xd8cfb7)
const ROPE = rgb(0x3d302e)
const FLAG = ramp(0x3a1218, 0x7a2328, 0xb23c34, 0xd8604a)
const LANTERN_CORE = rgb(0xfff0b8)
const LANTERN_PANE = rgb(0xffc56b)

const FOAM = rgb(0xc9d3e6)
const FOAM_DIM = rgb(0x7f8eac)
const RING = ramp(0x2c3a56, 0x4e5e80, 0x8292b4)

// ---- shared helpers ---------------------------------------------------------------------------

/**
 * Pick a step of `colors` from a continuous index, dithering only the last `soft` share of each
 * band into the next: a banded gradient with short dithered seams, never a full-field dither.
 */
const banded = (colors: number[], g: number, x: number, y: number, soft: number = 0.35): number => {
    const top = colors.length - 1
    if (g <= 0) return colors[0]
    if (g >= top) return colors[top]
    const i = Math.floor(g)
    const f = clamp01((g - i - (1 - soft)) / soft)
    return colors[bayer(x, y) < f ? i + 1 : i]
}

/** Logical to art pixel (the pixel that holds the point). */
const art = (p: Pt): Pt => [p[0] / PX, p[1] / PX]

/** Fill a polygon into a fresh mask buffer just big enough for it. */
const maskOf = (pts: Pt[]) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const p of pts) {
        x0 = Math.min(x0, p[0]); y0 = Math.min(y0, p[1]); x1 = Math.max(x1, p[0]); y1 = Math.max(y1, p[1])
    }
    const buf = new PixelBuffer(Math.ceil(x1) - Math.floor(x0) + 3, Math.ceil(y1) - Math.floor(y0) + 3, Math.floor(x0) - 1, Math.floor(y0) - 1)
    fillPolygon(pts, (x, y) => buf.set(x, y, 1))
    return { buf, inside: (x: number, y: number) => buf.get(x, y) !== 0, x0, x1, y0, y1 }
}

// ---- rocks ------------------------------------------------------------------------------------

/**
 * A rock in LPC's manner: lit along its top and left, a dark outline down its right and foot, the
 * shadowed right third a step darker, cracks drawn as shadow lines.
 */
const paintRock = (layer: PixelBuffer, outline: Pt[], cracks: Pt[][] = []) => {
    const pts = outline.map(art)
    const m = maskOf(pts)
    const { buf, inside } = m
    for (let y = buf.y0; y < buf.y0 + buf.h; y++) {
        for (let x = buf.x0; x < buf.x0 + buf.w; x++) {
            if (!inside(x, y)) continue
            const up = inside(x, y - 1), left = inside(x - 1, y), right = inside(x + 1, y), down = inside(x, y + 1)
            const rel = (x + 0.5 - m.x0) / Math.max(1, m.x1 - m.x0)
            let c: number
            if (!up) c = !left || rel < 0.45 ? ROCK[5] : ROCK[4]
            else if (!right || !down) c = ROCK[0]
            else if (!left) c = ROCK[3]
            else if (!inside(x, y - 2)) c = rel > 0.7 ? ROCK[3] : ROCK[4]
            else c = rel > 0.66 ? ROCK[1] : rel < 0.3 && !inside(x - 2, y) ? ROCK[3] : ROCK[2]
            layer.set(x, y, c)
        }
    }
    for (const crack of cracks) {
        drawPolyline(crack.map(art), (x, y) => {
            if (inside(x, y) && inside(x, y - 1) && inside(x + 1, y) && inside(x, y + 1)) {
                layer.set(x, y, ROCK[1])
                if (inside(x - 1, y - 1)) layer.set(x - 1, y, ROCK[3])
            }
        })
    }
}

// ---- the backdrop -----------------------------------------------------------------------------

/** A piece of the backdrop and when it dissolves in, by ordered dither. */
export interface RevealLayer {
    buf: PixelBuffer
    /** Sky pieces sit behind the scud and the lightning; the land is in front of them. */
    group?: "sky" | "land"
    /** The pines: once revealed, the scene swaps in Backdrop.pineFrames as they sway. */
    sway?: boolean
    from: number
    to: number
    /** Extra delay across the piece, 0..1: the horizon draws left to right, the hill from its foot. */
    sweep?: (x: number, y: number) => number
}

export interface Backdrop {
    /** Sky and sea, there from the first frame (under the fading loading screen). */
    base: PixelBuffer
    layers: RevealLayer[]
    /** The pines at each lean of their sway (PINE_LEANS); the reveal draws the middle one. */
    pineFrames: PixelBuffer[]
}

/** The art-pixel rectangle covering the visible stage, in authored coordinates. */
export const artRect = (b: Bounds) => {
    const x0 = Math.floor(b.left / PX)
    const y0 = Math.floor(b.top / PX)
    return { x0, y0, w: Math.ceil(b.right / PX) - x0, h: Math.ceil(b.bottom / PX) - y0 }
}

/** The moon, with no halo of its own: the storm's sky glows round the gap it shines through. */
const paintMoon = (layer: PixelBuffer) => {
    const cx = MOON.x / PX
    const cy = MOON.y / PX
    const r = MOON.r / PX
    const craters: [number, number, number][] = [[2.5, -4, 2.2], [-0.5, 3, 1.6], [5, 2.5, 1.5], [3.5, 6.5, 1.1], [6.5, -3.5, 1.1], [-2.5, -6, 1]]
    for (let y = Math.floor(cy - r - 1); y <= cy + r + 1; y++) {
        for (let x = Math.floor(cx - r - 1); x <= cx + r + 1; x++) {
            const dx = x + 0.5 - cx
            const dy = y + 0.5 - cy
            const d = Math.hypot(dx, dy)
            if (d > r + 0.3) continue
            // Gibbous, lit from the right: the dark limb is on the side away from the land.
            const term = -0.38 * r * Math.sqrt(Math.max(0, 1 - (dy / r) * (dy / r)))
            let c: number
            if (dx < term - 0.6) c = MOON_DARK
            else if (dx < term + 0.6) c = MOON_TERMINATOR
            else if (dx < term + 2.2) c = MOON_MID
            else if (d > r - 1.1 && dx > 0) c = MOON_RIM
            else c = MOON_LIT
            if (c === MOON_LIT || c === MOON_MID) {
                for (const [ox, oy, cr] of craters) {
                    const e = Math.hypot(dx - ox, dy - oy)
                    if (e < cr) c = e > cr - 0.9 && dx - ox > 0 && dy - oy > 0 ? MOON_RIM : MOON_CRATER
                }
            }
            layer.set(x, y, c)
        }
    }
}

/** A spruce in LPC's manner: stacked tiers, lit left, a shadow under each tier, outlined to the right. */
const paintPine = (layer: PixelBuffer, cx: number, by: number, h: number, seed: number, lean: number = 0) => {
    const tiers = h >= 19 ? 4 : 3
    const w = h * 0.36
    const pine = new PixelBuffer(Math.ceil(w) * 2 + 7, h + 4, cx - Math.ceil(w) - 3, by - h - 2)
    const top = by - h
    const bottom = by - 2
    for (let y = bottom; y <= by; y++) {
        pine.set(cx, y, TRUNK)
    }
    const spans: { yb: number; hl: number; hr: number }[] = []
    for (let j = tiers - 1; j >= 0; j--) {
        const yb = top + Math.round(((j + 1) / tiers) * (bottom - top))
        const yt = j === 0 ? top : top + Math.round((j / tiers) * (bottom - top)) - Math.max(1, Math.round(h * 0.14))
        const wb = w * (0.42 + (0.58 * (j + 1)) / tiers)
        let hl = 0
        let hr = 0
        for (let y = yt; y <= yb; y++) {
            const f = (y - yt + 1) / (yb - yt + 1)
            const hw = wb * f
            hl = Math.round(hw - (hash01(seed * 131 + y * 7 + j) < 0.3 ? 1 : 0))
            hr = Math.round(hw - (hash01(seed * 17 + y * 3 + j * 5) < 0.3 ? 1 : 0))
            for (let x = cx - hl; x <= cx + hr; x++) {
                const rel = (x - cx) / Math.max(1, hw)
                const light = 0.6 - 0.55 * rel - 0.3 * f + (bayer(x, y) - 0.5) * 0.25
                pine.set(x, y, light > 0.66 ? PINE[4] : light > 0.34 ? PINE[3] : light > 0.04 ? PINE[2] : PINE[1])
            }
        }
        spans.push({ yb, hl, hr })
    }
    // Each tier throws a shadow on the one under it.
    for (const s of spans) {
        for (let x = cx - s.hl + 1; x <= cx + s.hr; x++) {
            if (pine.get(x, s.yb + 1) && pine.get(x, s.yb + 1) !== TRUNK) pine.set(x, s.yb + 1, PINE[1])
        }
    }
    // Outline everything but the lit edges.
    const out = new PixelBuffer(pine.w, pine.h, pine.x0, pine.y0)
    out.data.set(pine.data)
    for (let y = pine.y0; y < pine.y0 + pine.h; y++) {
        for (let x = pine.x0; x < pine.x0 + pine.w; x++) {
            const c = pine.get(x, y)
            if (!c || c === TRUNK) continue
            if (!pine.get(x + 1, y) || !pine.get(x, y + 1)) out.set(x, y, PINE[0])
        }
    }
    // Bent by the wind: the crown pushed downwind (+x), the trunk's foot staying put.
    for (let j = 0; j < out.h; j++) {
        const y = out.y0 + j
        const dx = Math.round(lean * Math.pow(clamp01((by - y) / h), 1.6))
        for (let i = 0; i < out.w; i++) {
            const c = out.data[j * out.w + i]
            if (c) layer.set(out.x0 + i + dx, y, c)
        }
    }
}

/**
 * The land the ship never reaches: a grassy hill rising off a narrow beach, spruce along its
 * crown. Authored on the right; the scene mirrors it to the left of the card.
 */
const paintLand = (layer: PixelBuffer, right: number) => {
    const land = buildLand(right)
    const poly = land.poly.map(art)
    const m = maskOf(poly)
    const topAt = (x: number) => {
        const lx = (x + 0.5) * PX
        const p = land.profile
        for (let i = 1; i < p.length; i++) {
            if (lx <= p[i][0]) {
                const a = p[i - 1], b = p[i]
                return (a[1] + ((b[1] - a[1]) * (lx - a[0])) / (b[0] - a[0] || 1)) / PX
            }
        }
        return p[p.length - 1][1] / PX
    }
    const shoreAt = (x: number) => {
        const shore = land.shore
        const lx = (x + 0.5) * PX
        for (let i = 1; i < shore.length; i++) {
            if (lx <= shore[i][0]) {
                const a = shore[i - 1], b = shore[i]
                return (a[1] + ((b[1] - a[1]) * (lx - a[0])) / (b[0] - a[0] || 1)) / PX
            }
        }
        return shore[shore.length - 1][1] / PX
    }
    let lastRow = 0
    for (let x = m.buf.x0; x < m.buf.x0 + m.buf.w; x++) {
        const top = topAt(x)
        const sand = Math.floor(shoreAt(x)) - 1
        for (let y = m.buf.y0; y < m.buf.y0 + m.buf.h; y++) {
            if (!m.inside(x, y)) continue
            lastRow = Math.max(lastRow, y)
            if (y >= sand) {
                const k = y - sand
                layer.set(x, y, k === 0 ? SAND_HI : k === 1 ? SAND : SAND_WET)
                continue
            }
            const depth = y + 0.5 - top
            const g = clamp01((y - top) / Math.max(1, sand - top))
            let c: number
            if (depth < 1.2) c = hash01(x * 3 + 1) < 0.28 ? HILL[5] : HILL[4]
            else if (depth < 2.4) c = HILL[3]
            else c = banded([HILL[3], HILL[2], HILL[1]], g * 2.6, x, y, 0.5)
            layer.set(x, y, c)
        }
        // Grass tufts standing up off the crown.
        if (hash01(x * 7 + 3) < 0.3 && x > m.buf.x0 + 2) {
            layer.set(x, Math.floor(top) - 1, HILL[4])
        }
    }
    // The line art's hatching becomes darker grass streaks down the slope.
    for (const h of land.hatches) {
        drawPolyline(h.pts.map(art), (x, y) => {
            const c = layer.get(x, y)
            if (c === HILL[2] || c === HILL[3]) layer.set(x, y, HILL[1])
        })
    }
    return lastRow
}

/** The water's edge along the far beach, in art pixels, at an art column. */
export const shoreRowAt = (right: number) => {
    const shore = buildLand(right).shore
    return (x: number): number => {
        const lx = (x + 0.5) * PX
        for (let i = 1; i < shore.length; i++) {
            if (lx <= shore[i][0]) {
                const a = shore[i - 1], b = shore[i]
                return (a[1] + ((b[1] - a[1]) * (lx - a[0])) / (b[0] - a[0] || 1)) / PX
            }
        }
        return shore[shore.length - 1][1] / PX
    }
}

/** The spruce along the hill's crown, bent `lean` art pixels at the tip by the gale. */
const paintPines = (pines: PixelBuffer, right: number, lean: number) => {
    buildLand(right).pines.forEach((p, i) => {
        // pinePoints starts at the apex; its base is the trunk's foot.
        const apex = p.pts[0]
        const foot = p.pts[Math.floor(p.pts.length / 2)]
        const cx = Math.round(apex[0] / PX)
        const by = Math.round(foot[1] / PX)
        const h = Math.round((foot[1] - apex[1]) / PX)
        paintPine(pines, cx, by, h, i + 1, lean)
    })
}

/** How far the pines lean at each step of their sway in the gusts. */
export const PINE_LEANS = [1, 2, 3]

/** Which sway frame the pines are in at `t`: they lean further in the gusts. */
export const pineFrameAt = (t: number): number => {
    const g = Math.sin(t / 420) * 0.6 + Math.sin(t / 1300 + 1) * 0.6
    return g > 0.45 ? 2 : g < -0.45 ? 0 : 1
}

/**
 * Sky, moon and the far shore: everything that never moves once it is drawn in. The sky is the storm's
 * (pixel_storm_art); the sea under it is painted every frame (paintSwell), over the base's dark water.
 */
export const buildBackdrop = (b: Bounds): Backdrop => {
    const r = artRect(b)
    const make = () => new PixelBuffer(r.w, r.h, r.x0, r.y0)
    const base = make()
    base.draw(buildStormSky(b))
    for (let y = Math.max(r.y0, HORIZON_ROW); y < r.y0 + r.h; y++) {
        for (let x = r.x0; x < r.x0 + r.w; x++) base.set(x, y, SEA_BASE)
    }

    const moon = make()
    paintMoon(moon)

    const land = make()
    paintLand(land, b.right)
    // The pines at each lean of their sway in the gale.
    const pineFrames = PINE_LEANS.map(lean => {
        const pines = make()
        paintPines(pines, b.right, lean)
        return pines.cropped() ?? pines
    })

    const rocks = make()
    paintRock(rocks, ROCK_SHORE_A, [ROCK_SHORE_HATCH])
    paintRock(rocks, ROCK_SHORE_B)

    const landTop = 125
    const layers: RevealLayer[] = [
        { buf: moon, from: SCENE.moon[0], to: SCENE.moon[1] + 300, sweep: (x, y) => clamp01(Math.hypot(x - MOON.x / PX, y - MOON.y / PX) / 28) },
        { buf: land, group: "land", from: SCENE.land[0], to: SCENE.land[0] + 1300, sweep: (_x, y) => 1 - clamp01((y - landTop) / 56) },
        { buf: pineFrames[1], group: "land", sway: true, from: SCENE.land[0] + 800, to: SCENE.land[1], sweep: x => clamp01((x - 355) / 60) },
        { buf: rocks, group: "land", from: SCENE.rocks[0], to: SCENE.rocks[1] },
    ]
    // Each piece keeps only the pixels it painted: the dissolve then touches a fraction of the screen.
    return {
        base,
        pineFrames,
        layers: layers.flatMap(layer => {
            const buf = layer.buf.cropped()
            return buf ? [{ ...layer, buf }] : []
        }),
    }
}

/** Does this pixel of `layer` show at `t`? The dissolve: Bayer threshold, nudged by the sweep. */
const revealed = (layer: RevealLayer, p: number, x: number, y: number): boolean => {
    if (p >= 1) return true
    if (p <= 0) return false
    const sweep = layer.sweep ? layer.sweep(x, y) : 0
    return p > bayer(x, y) * 0.6 + clamp01(sweep) * 0.4
}

/**
 * The backdrop as it stands at `t`. `out` must cover the base. With a `group`, only that group's
 * pieces -- and the base only for the sky -- so the scud and the lightning can go between the two.
 * Without `pines`, the swaying pines are left to composePines.
 */
export const composeBackdrop = (out: PixelBuffer, backdrop: Backdrop, t: number, group?: "sky" | "land", pines: boolean = true) => {
    if (group !== "land") {
        out.data.set(backdrop.base.data)
    }
    for (const layer of backdrop.layers) {
        if (group && (layer.group ?? "sky") !== group) continue
        if (!pines && layer.sway) continue
        const p = prog(t, layer.from, layer.to)
        if (p <= 0) continue
        const buf = layer.buf
        for (let j = 0; j < buf.h; j++) {
            const y = buf.y0 + j
            for (let i = 0; i < buf.w; i++) {
                const c = buf.data[j * buf.w + i]
                if (c && revealed(layer, p, buf.x0 + i, y)) {
                    out.set(buf.x0 + i, y, c)
                }
            }
        }
    }
}

/** The swaying pines at `t` into `out` (cleared first): dissolving in, then leaning with the gusts. */
export const composePines = (out: PixelBuffer, backdrop: Backdrop, t: number) => {
    out.clear()
    const layer = backdrop.layers.find(l => l.sway)
    if (!layer) return
    const p = prog(t, layer.from, layer.to)
    if (p <= 0) return
    const buf = p >= 1 ? backdrop.pineFrames[pineFrameAt(t)] : layer.buf
    for (let j = 0; j < buf.h; j++) {
        const y = buf.y0 + j
        for (let i = 0; i < buf.w; i++) {
            const c = buf.data[j * buf.w + i]
            if (c && revealed(layer, p, buf.x0 + i, y)) out.set(buf.x0 + i, y, c)
        }
    }
}

/** The rectangle every sway frame of the pines fits in, for the layer they are drawn into. */
export const pinesRect = (backdrop: Backdrop) => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const f of backdrop.pineFrames) {
        x0 = Math.min(x0, f.x0); y0 = Math.min(y0, f.y0); x1 = Math.max(x1, f.x0 + f.w); y1 = Math.max(y1, f.y0 + f.h)
    }
    return { x0, y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }
}

/** Done dissolving: after this the backdrop never changes again. */
export const BACKDROP_DONE = Math.max(SCENE.horizon[1], SCENE.moon[1] + 300, SCENE.land[1], SCENE.rocks[1])

/** Light in stepped levels, not a smooth fade: pixel art blinks from one shade to the next. */
const steps = (a: number, n: number = 4): number => Math.round(clamp01(a) * n) / n

// ---- the ship ---------------------------------------------------------------------------------

/** The ship's sprite space, in art pixels around its anchor: room for the tallest pose at full size. */
const SHIP_BOX = { x0: -64, y0: -116, w: 140, h: 132 }

const ID = { rope: 1, mast: 2, hull: 3, sail: 4, spar: 9, top: 10, flag: 11, lantern: 12 }
/** Each sail gets its own id, so overlapping sails are outlined against each other. */
const SAIL_IDS = { mizzen: 4, mainCourse: 5, mainTop: 6, foreCourse: 7, foreTop: 8 }

const darkerCloth = (c: number): number => {
    const i = CLOTH.indexOf(c)
    return i > 1 ? CLOTH[i - 1] : CLOTH[1]
}

/**
 * The quantities that change what the ship looks like, rounded to what one art pixel can show.
 * Rasterizing from this -- not the raw state -- means the ship holds a pose until the change would
 * actually move a pixel, the way a hand-drawn sprite steps between frames.
 */
export interface ShipPose {
    s: number
    rot: number
    bend: number
    yard: number
    tear: number
    swing: number
    /** Animation time for the flapping canvas and the pennant: about ten frames a second. */
    frame: number
}

const q = (v: number, step: number) => Math.round(v / step) * step

export const shipPoseAt = (st: ShipState, t: number): ShipPose => ({
    s: q(st.s, 0.012),
    rot: q(Math.atan2(st.sin, st.cos), 0.009),
    bend: q(st.bend, 0.008),
    yard: q(st.yard, 0.035),
    tear: q(st.tear, 0.125),
    swing: q(st.swing, 0.06),
    frame: Math.floor(t / 50) * 50,
})

export const poseKey = (p: ShipPose): string => `${p.s}|${p.rot}|${p.bend}|${p.yard}|${p.tear}|${p.swing}|${p.frame}`

/** The ship state a pose stands for: at the origin, so sprite space is ship space over PX. */
const poseState = (p: ShipPose): ShipState => ({
    x: 0,
    y: 0,
    s: p.s,
    wl: 0,
    cos: Math.cos(p.rot),
    sin: Math.sin(p.rot),
    bend: p.bend,
    yard: p.yard,
    tear: p.tear,
    swing: p.swing,
    ink: 1,
    alpha: 1,
})

/** Shading for a square sail, lit from the left: bright on the luff, shadow in the leech and foot. */
const clothAt = (u: number, v: number, x: number, y: number): number => {
    const uu = clamp01(u)
    const vv = clamp01(v)
    const light = 0.97 - 0.62 * Math.pow(uu, 1.2) - 0.22 * vv * vv + 0.1 * Math.sin(Math.PI * vv) + (bayer(x, y) - 0.5) * 0.035
    return light > 0.8 ? CLOTH[5] : light > 0.58 ? CLOTH[4] : light > 0.38 ? CLOTH[3] : CLOTH[2]
}

/**
 * The ship in one pose, as a sprite around its anchor (the ship state's x, y). Hull, sails, spars and
 * rigging are filled pixel by pixel from the line art's own geometry, then outlined part by part.
 */
export const rasterizeShip = (pose: ShipPose): PixelBuffer => {
    const st = poseState(pose)
    const t = pose.frame
    const buf = new PixelBuffer(SHIP_BOX.w, SHIP_BOX.h, SHIP_BOX.x0, SHIP_BOX.y0)
    const ids = new Uint8Array(buf.w * buf.h)
    const put = (x: number, y: number, id: number, c: number) => {
        const i = buf.index(x, y)
        if (i >= 0) {
            buf.data[i] = c
            ids[i] = id
        }
    }
    const idAt = (x: number, y: number) => {
        const i = buf.index(x, y)
        return i < 0 ? 0 : ids[i]
    }
    type Pre = (x: number, y: number, s: ShipState) => Pt
    const to = (x: number, y: number, rig: boolean = false, pre?: Pre): Pt => {
        const p = pre ? pre(x, y, st) : ([x, y] as Pt)
        const o = shipPoint(st, p[0], p[1], rig)
        return [o[0] / PX, o[1] / PX]
    }
    const cs = st.cos
    const sn = st.sin
    /** Back from a sprite pixel to ship units, for shading. Ignores a `pre` swing (it is small). */
    const local = (px: number, py: number, rig: boolean): Pt => {
        const u = ((px + 0.5) * PX) / st.s - PIVOT[0]
        const v = ((py + 0.5) * PX) / st.s - PIVOT[1]
        let x = PIVOT[0] + u * cs + v * sn
        const y = PIVOT[1] - u * sn + v * cs
        if (rig) {
            const h = -31 - y
            if (h > 0) x -= (st.bend * h * h) / 170
        }
        return [x, y]
    }
    const thick = (units: number) => Math.max(1, Math.round((units * st.s) / PX))
    const spar = (a: Pt, b: Pt, units: number, id: number, lit: number, base: number, rig: boolean = true, pre?: Pre) => {
        const A = to(a[0], a[1], rig, pre)
        const B = to(b[0], b[1], rig, pre)
        const n = thick(units)
        const steep = Math.abs(B[1] - A[1]) > Math.abs(B[0] - A[0])
        for (let k = 0; k < n; k++) {
            const o = k - Math.floor((n - 1) / 2)
            drawLine(A[0] + (steep ? o : 0), A[1] + (steep ? 0 : o), B[0] + (steep ? o : 0), B[1] + (steep ? 0 : o), (x, y) =>
                put(x, y, id, n > 1 && k === 0 ? lit : base),
            )
        }
    }
    const rope = (d: string) => {
        const pts = flat(d).pts.map(p => to(p[0], p[1], true))
        drawPolyline(pts, (x, y) => put(x, y, ID.rope, ROPE))
    }
    const sail = (local0: Pt[], id: number, seams: string[], pre?: Pre) => {
        let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity
        for (const p of local0) {
            x0 = Math.min(x0, p[0]); x1 = Math.max(x1, p[0]); y0 = Math.min(y0, p[1]); y1 = Math.max(y1, p[1])
        }
        const poly = local0.map(p => to(p[0], p[1], true, pre))
        fillPolygon(poly, (x, y) => {
            const [lx, ly] = local(x, y, true)
            put(x, y, id, clothAt((lx - x0) / (x1 - x0), (ly - y0) / (y1 - y0), x, y))
        })
        for (const seam of seams) {
            drawPolyline(flat(seam).pts.map(p => to(p[0], p[1], true, pre)), (x, y) => {
                const i = buf.index(x, y)
                if (i >= 0 && ids[i] === id) buf.data[i] = darkerCloth(buf.data[i])
            })
        }
    }

    // Bowsprit and standing rigging, behind everything else.
    spar([78, -50], [130, -78], 3.5, ID.mast, WOOD[4], WOOD[3], false)
    rope("M 60 -168 L 128 -77")
    rope("M -6 -194 L 56 -54")
    rope("M -6 -196 L 60 -168")
    rope("M -62 -140 L -6 -112")
    rope("M -6 -190 L -40 -58")
    for (const [top, feet] of [
        [[-6, -150], [[-26, -33], [-12, -31], [8, -31]]],
        [[60, -131], [[47, -51], [73, -51]]],
        [[-62, -124], [[-73, -61], [-51, -60]]],
    ] as [Pt, Pt[]][]) {
        feet.forEach(f => rope(`M ${top[0]} ${top[1]} L ${f[0]} ${f[1]}`))
    }

    // Masts.
    spar([-62, -61], [-62, -142], 3.5, ID.mast, WOOD[4], WOOD[2])
    spar([-6, -31], [-6, -198], 4.5, ID.mast, WOOD[4], WOOD[2])
    spar([60, -51], [60, -170], 4, ID.mast, WOOD[4], WOOD[2])
    // The tops sit behind the topsails: drawn over them they read as a pair of eyes.
    for (const top of ["M -15 -152 L 3 -152 L 1 -146 L -13 -146 Z", "M 52 -132 L 68 -132 L 66 -127 L 54 -127 Z"]) {
        fillPolygon(flat(top).pts.map(p => to(p[0], p[1], true)), (x, y) => put(x, y, ID.top, WOOD[3]))
    }

    // The hull: banded darker toward the water, the stern catching the moon, a balustrade on each castle.
    const hull = flat("M -84 -62 L -44 -60 L -44 -36 Q 0 -28 48 -34 L 48 -50 L 88 -52 C 92 -24 84 0 64 14 C 40 24 -30 26 -60 18 L -72 8 Z").pts
    const aftTop = (x: number) => -62 + (x + 84) / 20
    const foreTop = (x: number) => -50 - (x - 48) / 20
    fillPolygon(
        hull.map(p => to(p[0], p[1])),
        (x, y) => {
            const [lx, ly] = local(x, y, false)
            let step = ly > -6 ? 1 : ly > -19 ? 2 : 3
            if (lx < -74 && ly < -14) step++
            let c = WOOD[step]
            const aft = lx < -45 && ly < -54 && ly > aftTop(lx) + 1.5
            const fore = lx > 49 && ly < -46 && ly > foreTop(lx) + 1.5
            if (aft || fore) c = x % 2 === 0 ? WOOD[1] : WOOD[4]
            put(x, y, ID.hull, c)
        },
    )
    const overHull = (d: string, color: number, dy: number = 0) =>
        drawPolyline(flat(d).pts.map(p => to(p[0], p[1])), (x, y) => {
            if (idAt(x, y + dy) === ID.hull) put(x, y + dy, ID.hull, color)
        })
    overHull("M -78 -26 Q 0 -6 88 -24", WOOD[5], -1)
    overHull("M -78 -26 Q 0 -6 88 -24", WOOD[1])
    overHull("M -75 -8 Q 0 10 83 -7", WOOD[1])
    overHull("M -44 -33 Q 0 -25 48 -31", WOOD[4])
    overHull("M -83.5 -54 L -44 -53", WOOD[1])
    overHull("M 48 -45 L 89 -46", WOOD[1])
    const port = (x: number, y: number, size: number) => {
        const sq: Pt[] = [[x, y], [x + size, y], [x + size, y + size], [x, y + size]].map(p => to(p[0], p[1]))
        let any = false
        fillPolygon(sq, (px, py) => {
            any = true
            put(px, py, ID.hull, WOOD[0])
        })
        if (!any) {
            const c = to(x + size / 2, y + size / 2)
            put(Math.floor(c[0]), Math.floor(c[1]), ID.hull, WOOD[0])
        }
    }
    ;[-32, -17, -2, 13, 28].forEach(x => port(x, -27.5, 5.5))
    port(-68, -48, 5)
    port(-57, -48, 5)
    port(60, -43, 5)
    port(71, -43, 5)

    // Sails, back to front.
    sail(flat("M -30 -78 L -96 -160 C -92 -120 -86 -90 -82 -66 Q -56 -66 -30 -78 Z").pts, SAIL_IDS.mizzen, [])
    spar([-26, -73], [-100, -165], 2.5, ID.spar, WOOD[3], WOOD[2])
    sail(flat("M -52 -121 L 40 -121 C 48 -103 48 -84 42 -68 Q -6 -60 -54 -68 C -60 -84 -60 -103 -52 -121 Z").pts, SAIL_IDS.mainCourse, [
        "M -26 -120 C -32 -100 -32 -82 -28 -64",
        "M 14 -120 C 18 -100 18 -82 16 -64",
    ])
    sail(flat("M -33 -173 L 21 -173 C 27 -160 27 -140 23 -128 Q -6 -123 -35 -128 C -39 -140 -39 -160 -33 -173 Z").pts, SAIL_IDS.mainTop, [
        "M -14 -172 C -17 -158 -17 -142 -15 -126",
        "M 4 -172 C 6 -158 6 -142 5 -126",
    ])
    const foreCourse = foreCoursePoints(st, t)
    sail(foreCourse, SAIL_IDS.foreCourse, st.tear < 0.5 ? ["M 46 -104 C 41 -88 41 -72 44 -57", "M 74 -104 C 78 -88 78 -72 76 -57"] : ["M 46 -104 C 41 -88 41 -72 44 -57"])
    const topsail = yardSwing(topsailWeight)
    sail(
        flat("M 38 -149 L 82 -149 C 87 -138 87 -122 84 -111 Q 60 -107 36 -111 C 33 -122 33 -138 38 -149 Z").pts,
        SAIL_IDS.foreTop,
        ["M 52 -148 C 50 -136 50 -124 51 -110", "M 68 -148 C 70 -136 70 -124 69 -110"],
        topsail,
    )

    // Yards and tops.
    spar([-56, -122], [44, -122], 3, ID.spar, WOOD[3], WOOD[2])
    spar([-36, -174], [24, -174], 2.6, ID.spar, WOOD[3], WOOD[2])
    spar([22, -106], [98, -106], 3, ID.spar, WOOD[3], WOOD[2])
    spar([36, -150], [84, -150], 2.6, ID.spar, WOOD[3], WOOD[2], true, yardSwing(() => 1))

    // The pennant at the main truck.
    fillPolygon(pennantPoints(st, t).map(p => to(p[0], p[1], true)), (x, y) => put(x, y, ID.flag, FLAG[2]))

    // Outlines, part against part: a sail is edged where it meets the sky, a spar or another sail.
    const edged = buf.data.slice()
    for (let j = 0; j < buf.h; j++) {
        for (let i = 0; i < buf.w; i++) {
            const id = ids[j * buf.w + i]
            if (id < ID.hull || id === ID.spar) continue
            const x = buf.x0 + i
            const y = buf.y0 + j
            const up = idAt(x, y - 1) !== id
            const edge = up || idAt(x + 1, y) !== id || idAt(x, y + 1) !== id || idAt(x - 1, y) !== id
            if (!edge) continue
            let c: number
            if (id === ID.hull) c = up ? WOOD[4] : WOOD[0]
            else if (id === ID.flag) c = up ? FLAG[3] : FLAG[0]
            else if (id === ID.top) c = up ? WOOD[4] : WOOD[0]
            else c = up && idAt(x, y - 1) === 0 ? CLOTH[3] : CLOTH[1]
            edged[j * buf.w + i] = c
        }
    }
    buf.data.set(edged)
    return buf
}

/** The stern lantern, drawn over the ship at its real position (it swings on its own clock). */
const paintLantern = (out: PixelBuffer, st: ShipState, t: number, ax: number, ay: number) => {
    const lantern = lanternAt(st, t)
    if (lantern.drawn <= 0) return
    const sp = (p: Pt): Pt => [(p[0] - st.x) / PX + ax, (p[1] - st.y) / PX + ay]
    drawPolyline(lantern.bracket.map(sp), (x, y) => out.set(x, y, WOOD[0]))
    const body = lantern.body.map(sp)
    const lit = lantern.lit > 0
    let any = false
    fillPolygon(body, (x, y) => {
        any = true
        out.set(x, y, lit ? LANTERN_PANE : WOOD[1])
    })
    const g = sp(lantern.glow)
    if (!any || lit) out.set(Math.floor(g[0]), Math.floor(g[1]), lit ? LANTERN_CORE : WOOD[1])
    const cap = lantern.cap.map(sp)
    drawPolyline(cap, (x, y) => out.set(x, y, WOOD[0]))
}

// ---- the live layer ---------------------------------------------------------------------------

/** The part of the scene that moves, in authored art pixels: the ship's whole voyage and its spray. */
export const LIVE_BOX = { x0: -44, y0: 78, w: 188, h: 148 }

export const buildRocks = (): PixelBuffer => {
    const buf = new PixelBuffer(LIVE_BOX.w, LIVE_BOX.h, LIVE_BOX.x0, LIVE_BOX.y0)
    paintRock(buf, ROCK_BOW, ROCK_BOW_HATCH.slice(0, 5))
    paintRock(buf, ROCK_NEAR, ROCK_BOW_HATCH.slice(5))
    return buf
}

/**
 * The ship is drawn at keyframes this far apart (choreography ms) and cross-faded from each into the
 * next. Every keyframe is a crisp raster; only the change between two of them fades, so a step up in
 * size, a pixel of travel or the bob glides instead of jumping. 40 is 80ms of real time at INTRO_PACE.
 */
export const SHIP_KEY_MS = 40

/** The ship as she stands at one keyframe. */
export interface ShipFrame {
    t: number
    st: ShipState
    sprite: PixelBuffer
    /** Where the sprite's anchor sits, and the water's row, in art pixels. */
    ax: number
    ay: number
    wlRow: number
    /** 0..1 while she emerges from the dark by dither. */
    appear: number
    /** How far her colours are taken toward the night haze: farther out, hazier. */
    haze: number
}

/**
 * The ship's keyframes, and the last rasterized pose: she is only re-rasterized when a pixel of her
 * would change, and each keyframe is built once.
 */
export class ShipSprites {
    /** Scratch layers the two keyframes are painted into before they are cross-faded. */
    readonly scratch = [0, 1].map(() => new PixelBuffer(LIVE_BOX.w, LIVE_BOX.h, LIVE_BOX.x0, LIVE_BOX.y0))
    private key = ""
    private sprite?: PixelBuffer
    private readonly frames = new Map<number, ShipFrame>()

    at = (pose: ShipPose): PixelBuffer => {
        const key = poseKey(pose)
        if (key !== this.key || !this.sprite) {
            this.sprite = rasterizeShip(pose)
            this.key = key
        }
        return this.sprite
    }

    frameAt = (t: number): ShipFrame => {
        const cached = this.frames.get(t)
        if (cached) {
            return cached
        }
        const st = shipStateAt(t)
        const wlRow = Math.round(st.wl / PX)
        const near = smooth((st.s - SHIP_FAR.s) / (SHIP_AGROUND.s - SHIP_FAR.s))
        const frame: ShipFrame = {
            t,
            st,
            sprite: this.at(shipPoseAt(st, t)),
            ax: Math.round(st.x / PX),
            // The bob, a whole pixel at a time (made a touch bigger than the line art's, to show at all).
            ay: wlRow + Math.round(((st.y - st.wl) * 1.6) / PX),
            wlRow,
            appear: prog(t, SCENE.shipInk[0], SCENE.shipInk[0] + 900),
            haze: q((1 - near) * 0.45, 0.075),
        }
        this.frames.set(t, frame)
        if (this.frames.size > 3) {
            this.frames.delete(this.frames.keys().next().value)
        }
        return frame
    }
}

/** The storm's haze on the horizon: far out, she is half lost in it. */
const HAZE = rgb(0x3d3748)

/** One keyframe of the ship: hull and sails above the water, and the lantern. */
const paintShipFrame = (out: PixelBuffer, f: ShipFrame) => {
    const { sprite, ax, ay, wlRow, appear, haze } = f
    const w = sprite.w
    for (let j = 0; j < sprite.h; j++) {
        const y = ay + sprite.y0 + j
        if (y >= wlRow) break
        for (let i = 0; i < w; i++) {
            const c = sprite.data[j * w + i]
            if (!c) continue
            if (appear < 1 && appear <= bayer(i, j)) continue
            out.set(ax + sprite.x0 + i, y, haze > 0 ? mix(c, HAZE, haze) : c)
        }
    }
    // No reflection: the sea is far too rough to hold one. The near swell washes over her instead.
    paintLantern(out, f.st, f.t, ax, ay)
}


/**
 * Warm light ADDED to what is there, the way the game's fire light works: blended at a low alpha
 * over the night blue, orange only mixes to grey. Where the layer is empty the sea under it is lit
 * instead, which makes the pixel opaque. (The scene adds its light on a layer of its own: lightInto.)
 */
const addLight = (out: PixelBuffer, x: number, y: number, amount: number) => {
    const i = out.index(x, y)
    if (i < 0 || amount <= 0) return
    const c = out.data[i] || SEA_BASE
    const r = Math.min(255, (c & 0xff) + 255 * amount)
    const g = Math.min(255, ((c >>> 8) & 0xff) + 184 * amount)
    const b = Math.min(255, ((c >>> 16) & 0xff) + 96 * amount)
    out.data[i] = ((0xff << 24) | (Math.round(b) << 16) | (Math.round(g) << 8) | Math.round(r)) >>> 0
}

/** The same light as colour to be added: a layer drawn with an ADD blend over everything under it. */
const lightInto = (light: PixelBuffer) => (_out: PixelBuffer, x: number, y: number, amount: number) => {
    const i = light.index(x, y)
    if (i < 0 || amount <= 0) return
    const c = light.data[i]
    const r = Math.min(255, (c & 0xff) + 255 * amount)
    const g = Math.min(255, ((c >>> 8) & 0xff) + 184 * amount)
    const b = Math.min(255, ((c >>> 16) & 0xff) + 96 * amount)
    light.data[i] = ((0xff << 24) | (Math.round(b) << 16) | (Math.round(g) << 8) | Math.round(r)) >>> 0
}

export interface LiveOptions {
    /** The storm's swell: the rows in front of her waterline are painted over her hull. */
    swell?: SwellRow[]
    /** Where the lantern's light goes, as colour to ADD (same rectangle as `out`); else it is mixed in. */
    light?: PixelBuffer
    /** False for a player who asked for reduced motion: no lightning, so nothing goes to silhouette. */
    lightning?: boolean
}

const ringColor = (fade: number) => (fade < 0.34 ? RING[2] : fade < 0.67 ? RING[1] : RING[0])

/**
 * Everything that moves, painted into `out` (which covers LIVE_BOX): the ship, the rocks in front of
 * her, the swell washing over both, white water, spray and the lantern's light. Returns the ship's
 * state, for the scene's shake.
 */
export const paintLive = (out: PixelBuffer, t: number, rocks: PixelBuffer, ships: ShipSprites, opts: LiveOptions = {}): ShipState => {
    out.clear()
    opts.light?.clear()
    const swell = opts.swell
    const light = opts.light ? lightInto(opts.light) : addLight
    const st = shipStateAt(t)

    const shipShows = t >= SCENE.shipInk[0]
    let ax = 0
    let ay = 0
    if (shipShows) {
        // The keyframes either side of now, the later one faded in over the earlier.
        const k0 = Math.floor(t / SHIP_KEY_MS) * SHIP_KEY_MS
        const from = ships.frameAt(k0)
        const to = ships.frameAt(k0 + SHIP_KEY_MS)
        const w = (t - k0) / SHIP_KEY_MS
        const [a, b] = ships.scratch
        a.clear()
        paintShipFrame(a, from)
        b.clear()
        if (w > 0) {
            paintShipFrame(b, to)
        }
        crossFade(out, a, b, w)
        ax = lerp(from.ax, to.ax, w)
        ay = lerp(from.ay, to.ay, w)
        const wlRow = Math.round(st.wl / PX)
        const appear = prog(t, SCENE.shipInk[0], SCENE.shipInk[0] + 900)
        // A ruffle of foam where the hull meets the water, and a bow wave and wake while under way.
        const drawn = appear
        if (drawn > 0.4) {
            const bow = shipPoint(st, 75, 0)
            const stern = shipPoint(st, -73, 0)
            const step = Math.floor(t / 200)
            for (let x = Math.round(stern[0] / PX); x <= Math.round(bow[0] / PX); x++) {
                if ((x + step) % 4 !== 0) out.set(x, wlRow, (x + step) % 7 === 0 ? FOAM : FOAM_DIM)
            }
            const way = prog(t, SCENE.sail[0], SCENE.sail[0] + 700) * (1 - prog(t, SCENE.impact, SCENE.impact + 450))
            if (way > 0.3) {
                const bx = Math.round(bow[0] / PX)
                const flick = Math.floor(t / 90) % 2
                out.set(bx + 1, wlRow - 1, FOAM)
                out.set(bx, wlRow - 1 - flick, FOAM)
                out.set(bx - 1, wlRow + 1, FOAM_DIM)
                out.set(bx - 3, wlRow + 1, FOAM_DIM)
                const sx = Math.round(stern[0] / PX)
                const off = Math.floor(t / 60) % 4
                for (let k = 1; k < Math.round(20 * st.s); k++) {
                    if ((k + off) % 4 < 2) out.set(sx - k, wlRow + (k > 10 ? 0 : 1), k < 8 ? FOAM_DIM : RING[1])
                }
            }
        }
    }

    // The rocks the bow rides up, in front of the hull.
    const rp = prog(t, SCENE.rocks[0], SCENE.rocks[1])
    if (rp > 0) {
        for (let j = 0; j < rocks.h; j++) {
            for (let i = 0; i < rocks.w; i++) {
                const c = rocks.data[j * rocks.w + i]
                if (c && (rp >= 1 || rp > bayer(i, j))) out.data[j * out.w + i] = c
            }
        }
        // White water: the sea bursting over the rock when she strikes, and every breaker after.
        const wash = Math.min(t - SCENE.impact, t - lastBreaker(t))
        if (wash >= 0 && wash < 700) {
            const rise = Math.sin(Math.PI * Math.min(1, wash / 700)) * (wash < 200 ? wash / 200 : 1)
            const line = 200 - Math.round(rise * 14)
            for (let j = 0; j < rocks.h; j++) {
                const y = rocks.y0 + j
                if (y < line) continue
                for (let i = 0; i < rocks.w; i++) {
                    if (!rocks.data[j * rocks.w + i]) continue
                    const x = rocks.x0 + i
                    const k = hash01(x * 17 + y * 3 + Math.floor(wash / 90))
                    if (k < 0.8 - (y - line) * 0.02) out.set(x, y, k < 0.35 ? FOAM : FOAM_DIM)
                }
            }
        }
        const foam = prog(t, SCENE.rocks[1], SCENE.rocks[1] + 600)
        if (foam > 0.3) {
            const l = Math.round((7 + 3 * Math.sin(t / 900)) / PX)
            for (let k = 0; k < l; k++) out.set(63 - k, 200, k % 2 ? FOAM_DIM : FOAM)
            for (let k = 0; k < Math.round(l * 0.6); k++) out.set(51 - k, 200, FOAM_DIM)
        }
    }

    // Lightning behind her: the ship and the rocks go to silhouette.
    const flash = opts.lightning === false ? undefined : flashAt(t)
    if (flash) {
        for (let i = 0; i < out.data.length; i++) {
            const c = out.data[i]
            if (c) out.data[i] = flashSolid(c, flash.level(out.x0 + (i % out.w), out.y0 + Math.floor(i / out.w)))
        }
    }

    // The swell in front of her waterline, over her hull and the rocks' feet.
    if (swell) {
        paintSwell(out, swell, t, rowAt(swell, st.wl / PX), Infinity, 1, flash)
    }

    // White water bursting up off the rock: foam balls, ragged at their edges, thinning as they fall.
    for (const puff of plumeAt(t)) {
        const cx = puff.x / PX
        const cy = puff.y / PX
        const r = puff.r / PX
        for (let y = Math.floor(cy - r); y <= cy + r; y++) {
            for (let x = Math.floor(cx - r); x <= cx + r; x++) {
                const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy) / r
                if (d > 1 || bayer(x, y) > puff.life * 1.4 - d * 0.5) continue
                out.set(x, y, d < 0.55 && puff.life > 0.45 && y < cy ? FOAM : FOAM_DIM)
            }
        }
    }

    // Spray off the bow.
    for (const drop of sprayAt(t)) {
        const x = Math.floor(drop.to[0] / PX)
        const y = Math.floor(drop.to[1] / PX)
        out.set(x, y, drop.alpha > 0.45 ? FOAM : FOAM_DIM)
        const fx = Math.floor(drop.from[0] / PX)
        const fy = Math.floor(drop.from[1] / PX)
        if ((fx !== x || fy !== y) && drop.alpha > 0.3) out.set(fx, fy, FOAM_DIM)
    }

    // The lantern's light: stepped rings of warm light, and its broken reflection on the water.
    if (shipShows) {
        const lantern = lanternAt(st, t)
        if (lantern.lit > 0) {
            const gx = (lantern.glow[0] - st.x) / PX + ax
            const gy = (lantern.glow[1] - st.y) / PX + ay
            const grow = (st.s / SHIP_AGROUND.s) * (1 + 0.6 * lantern.flare)
            const level = (0.55 * lantern.flicker + 0.4 * lantern.flare) * lantern.lit
            const radii = [2.2, 4.4, 7.2].map(r => r * grow)
            const amounts = [0.5, 0.24, 0.1]
            const reach = Math.ceil(radii[2]) + 1
            for (let y = Math.floor(gy - reach); y <= gy + reach; y++) {
                for (let x = Math.floor(gx - reach); x <= gx + reach; x++) {
                    const d = Math.hypot(x + 0.5 - gx, y + 0.5 - gy) + (bayer(x, y) - 0.5) * 1.4
                    const r = radii.findIndex(rr => d < rr)
                    if (r >= 0) light(out, x, y, steps(amounts[r] * level, 20))
                }
            }
            const wl = Math.round(st.wl / PX)
            for (let i = 0; i < 5; i++) {
                const y = wl + 1 + i * 2
                const hw = Math.round((3.4 - i * 0.5) * (0.75 + 0.35 * Math.sin(t / 140 + i * 1.9)) * (st.s / SHIP_AGROUND.s) / PX * 1.6)
                const x = Math.round(gx + Math.sin(t / 230 + i * 1.3) * 0.8)
                for (let k = -hw; k <= hw; k++) light(out, x + k, y, steps(0.45 * (1 - i / 5.5) * lantern.flicker * lantern.lit, 10))
            }
        }
    }
    return st
}

